import type { ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { ApiClientProvider } from "@/shared/api/ApiClientProvider";
import { BootstrapProvider } from "@/features/bootstrap/BootstrapProvider";
import { AuthProvider } from "@/features/auth/AuthProvider";

// Loaded from VITE_GOOGLE_CLIENT_ID at build time. Falsy when unset (e.g. a
// local checkout without the env var) - we still mount the provider so the app
// boots; the Google button is only rendered when this is present (AuthEntryPage),
// so email/password and invite-code flows keep working without it.
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? "";

// One client per browser tab, instantiated lazily and held for the lifetime
// of the page. Defaults tuned for our backend latency:
//   - staleTime 60s: navigating tabs within a minute renders cached data
//     instantly, no refetch. Past 60s we revalidate in the background.
//   - gcTime 5min: cache entries are dropped after 5min of zero observers.
//   - refetchOnWindowFocus on: third-party flows (Gmail OAuth, Chrome
//     extension reconnect) change app state while the user is off-tab; when
//     they return, revalidate so the dashboard reflects the new state
//     instead of showing the cached pre-flow snapshot.
//   - retry 1: backend already has Sentry; one retry covers blips without
//     hiding persistent failures.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});

export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <QueryClientProvider client={queryClient}>
        <ApiClientProvider>
          <BootstrapProvider>
            <AuthProvider>{children}</AuthProvider>
          </BootstrapProvider>
        </ApiClientProvider>
      </QueryClientProvider>
    </GoogleOAuthProvider>
  );
}
